import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import hotelService from '../services/hotelService';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import ImageZoomPreviewModal from '../components/common/ImageZoomPreviewModal';

const HotelCreatePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    accommodation_type: 'hotel',
    description: '',
    address: '',
    city: '',
    area: '',
    latitude: '',
    longitude: '',
    nearby_places: '',
    stars: 3,
    family_friendly: false,
    pet_friendly: false,
    parking_available: false,
    wifi_available: false,
    restaurant: false,
    swimming_pool: false,
    gym: false,
    spa: false,
    free_cancellation: false,
    no_credit_card_needed: false,
    breakfast_included: false,
    dinner_included: false,
    price_per_night: '',
    currency: 'PKR',
    contact_phone: '',
    contact_email: user?.email || '',
    website: '',
  });

  const [images, setImages] = useState([]);
  const [activePreviewIndex, setActivePreviewIndex] = useState(null);
  const { locLoading, captureCurrentLocation } = useCurrentLocation({
    onSuccess: ({ latitude, longitude }) => {
      setFormData((prev) => ({ ...prev, latitude, longitude }));
      setError(null);
    },
    onError: (message) => {
      setError(message);
    },
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/hotels/create' } });
      return;
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});
    setSuccessMessage(null);

    try {
      // Create FormData for multipart upload
      const data = new FormData();
      
      // Add all text fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          data.append(key, formData[key]);
        }
      });

      // Add images
      images.forEach((image) => {
        data.append('images', image);
      });

      const response = await hotelService.createHotel(data);
      setSuccessMessage('Hotel listing created successfully! It will be reviewed by our team.');
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/points');
      }, 2000);
      
    } catch (err) {
      // Reset field errors
      setFieldErrors({});
      
      // Handle validation errors with proper message extraction
      let errorMessage = 'Failed to create hotel listing. Please try again.';
      const newFieldErrors = {};
      
      // Check if error was transformed by axios interceptor (AppError)
      // AppError stores data in err.details.data
      let errorData = null;
      let status = null;
      
      if (err.details?.data) {
        // Error was transformed by handleApiError
        errorData = err.details.data;
        status = err.details.status;
      } else if (err.response?.data) {
        // Original axios error
        errorData = err.response.data;
        status = err.response.status;
      }
      
      if (errorData) {
        // Handle 400 Bad Request (Validation errors)
        if (status === 400) {
          // Extract field-specific errors
          Object.keys(errorData).forEach(field => {
            const fieldError = errorData[field];
            if (Array.isArray(fieldError)) {
              newFieldErrors[field] = fieldError[0];
            } else if (typeof fieldError === 'string') {
              newFieldErrors[field] = fieldError;
            } else if (typeof fieldError === 'object' && fieldError.message) {
              newFieldErrors[field] = fieldError.message;
            }
          });
          
          // Set main error message from field errors or general error
          if (newFieldErrors.name) {
            // Duplicate validation error is typically on 'name' field
            errorMessage = newFieldErrors.name;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (Object.keys(newFieldErrors).length > 0) {
            // Use first field error as main message
            const firstField = Object.keys(newFieldErrors)[0];
            errorMessage = newFieldErrors[firstField];
          }
        }
        // Handle 401 Unauthorized
        else if (status === 401) {
          errorMessage = 'You must be logged in to create a hotel listing. Please log in and try again.';
        }
        // Handle 403 Forbidden
        else if (status === 403) {
          errorMessage = errorData.detail || errorData.message || 'You do not have permission to create a hotel listing.';
        }
        // Handle 500 Server Error
        else if (status >= 500) {
          errorMessage = 'Server error occurred. Please try again later. If the problem persists, contact support.';
        }
        // Handle other status codes
        else {
          errorMessage = errorData.detail || errorData.message || `Error ${status}: Failed to create hotel listing.`;
        }
      }
      // If error has a message (from AppError or other)
      else if (err.message) {
        errorMessage = err.message;
      }
      // Handle network errors
      else if (err.request) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      setError(errorMessage);
      setFieldErrors(newFieldErrors);
      console.error('Error creating hotel:', err);
      console.error('Error details:', err.details);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data || err.details?.data);
      console.error('Field errors:', newFieldErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout title="List Your Property">
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">List Your Property</h1>
            </div>
            <p className="text-gray-600">
              Fill out the form below to list your point (hotel, guest house, or other accommodation) on PakPoints.
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-1">Error</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      fieldErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Pearl Continental Hotel"
                  />
                  {fieldErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Accommodation Type *
                  </label>
                  <select
                    name="accommodation_type"
                    value={formData.accommodation_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="hotel">Hotel</option>
                    <option value="guest_house">Guest House</option>
                    <option value="resort">Resort</option>
                    <option value="motel">Motel</option>
                    <option value="hostel">Hostel</option>
                    <option value="apartment">Serviced Apartment</option>
                    <option value="lodge">Lodge</option>
                    <option value="bnb">Bed & Breakfast</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Star Rating *
                  </label>
                  <select
                    name="stars"
                    value={formData.stars}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="1">1 Star</option>
                    <option value="2">2 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="5">5 Stars</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe your property, amenities, and what makes it special..."
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Location</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Lahore"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Area/Neighborhood (optional)
                  </label>
                  <input
                    type="text"
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., DHA Phase 5, Gulberg"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Full address"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nearby Places (optional)
                  </label>
                  <input
                    type="text"
                    name="nearby_places"
                    value={formData.nearby_places}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Airport, Mall, Beach (comma-separated)"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    List nearby attractions or places, separated by commas
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude (optional)
                  </label>
                  <input
                    type="text"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 31.5204"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude (optional)
                  </label>
                  <input
                    type="text"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 74.3587"
                  />
                </div>
                <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <span className="block text-sm font-medium text-gray-900">Precise location (optional)</span>
                      <span className="block text-xs text-gray-500">
                        Capture current GPS location for better map accuracy.
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={captureCurrentLocation}
                      disabled={locLoading}
                      className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {locLoading ? 'Getting location…' : 'Use Current Location'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Amenities & Features</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: 'family_friendly', label: 'Family Friendly' },
                  { name: 'pet_friendly', label: 'Pet Friendly' },
                  { name: 'parking_available', label: 'Parking' },
                  { name: 'wifi_available', label: 'WiFi' },
                  { name: 'restaurant', label: 'Restaurant' },
                  { name: 'swimming_pool', label: 'Swimming Pool' },
                  { name: 'gym', label: 'Gym' },
                  { name: 'spa', label: 'Spa' },
                ].map(({ name, label }) => (
                  <label key={name} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name={name}
                      checked={formData[name]}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Booking Benefits */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Booking Benefits</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: 'free_cancellation', label: 'Free Cancellation' },
                  { name: 'no_credit_card_needed', label: 'No Credit Card Needed!' },
                  { name: 'breakfast_included', label: 'Breakfast Included' },
                  { name: 'dinner_included', label: 'Dinner Included' },
                ].map(({ name, label }) => (
                  <label key={name} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name={name}
                      checked={formData[name]}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Pricing</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price Per Night (optional)
                  </label>
                  <input
                    type="number"
                    name="price_per_night"
                    value={formData.price_per_night}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 5000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="PKR">PKR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone *
                  </label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+92 xxx xxxxxxx"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="contact@example.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website (optional)
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://www.example.com"
                  />
                </div>
              </div>
            </div>

            {/* Images */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Images</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Photos
                </label>
                <input
                  type="file"
                  onChange={handleImageChange}
                  multiple
                  accept="image/*"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Upload multiple photos of your property. First image will be used as the main photo.
                </p>
                {images.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm text-green-600">
                      {images.length} image(s) selected
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {images.map((image, index) => (
                        <button
                          key={`${image.name}-${index}`}
                          type="button"
                          onClick={() => setActivePreviewIndex(index)}
                          className="block w-full text-left rounded-md overflow-hidden border border-gray-200"
                          title="Open zoom preview"
                        >
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`Selected ${index + 1}`}
                            className="w-full h-20 object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Listing'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/points')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Info Note */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Your listing will be reviewed by our team before being published. 
              This usually takes 24-48 hours. You'll be notified once it's live.
            </p>
          </div>
        </div>
      </div>
      <ImageZoomPreviewModal
        isOpen={activePreviewIndex !== null && activePreviewIndex >= 0}
        imageUrl={
          activePreviewIndex !== null && images[activePreviewIndex]
            ? URL.createObjectURL(images[activePreviewIndex])
            : ''
        }
        imageName={activePreviewIndex !== null ? images[activePreviewIndex]?.name : ''}
        onClose={() => setActivePreviewIndex(null)}
      />
    </PageLayout>
  );
};

export default HotelCreatePage;

